import React from 'react';

/**
 * ConsentResult page
 *
 * Displayed after a parent clicks the consent verification link.
 * The verify-consent Edge Function redirects here with a status param.
 *
 * URL: /consent-result?status=verified&name=Alex
 * URL: /consent-result?status=revoked
 * URL: /consent-result?status=invalid
 * URL: /consent-result?status=already_verified
 */

const ConsentResult: React.FC = () => {
  const params = new URLSearchParams(window.location.search);
  const status = params.get('status') || 'unknown';
  const childName = params.get('name') || 'your child';

  const getContent = () => {
    switch (status) {
      case 'verified':
        return {
          emoji: '🎉',
          title: 'Consent Confirmed!',
          message: `${childName} can now use ElevenUp. They're all set to start their 11+ preparation!`,
          subMessage: 'You can close this page and let them open the app — it will update automatically.',
          color: '#4FCCC6',
        };
      case 'already_verified':
        return {
          emoji: '✅',
          title: 'Already Verified',
          message: 'Your consent was already confirmed. No further action needed!',
          subMessage: 'You can close this page.',
          color: '#4FCCC6',
        };
      case 'revoked':
        return {
          emoji: '🔒',
          title: 'Consent Revoked',
          message: `Your consent has been withdrawn. ${childName}'s access to ElevenUp has been paused.`,
          subMessage: 'If you change your mind, you can create a new account or contact us.',
          color: '#F56565',
        };
      case 'invalid':
        return {
          emoji: '❌',
          title: 'Invalid Link',
          message: 'This consent verification link is invalid or has expired.',
          subMessage: 'Please check your email for a more recent link, or sign in to the app and request a new one.',
          color: '#F56565',
        };
      default:
        return {
          emoji: '❓',
          title: 'Something went wrong',
          message: 'We couldn\'t process your consent verification.',
          subMessage: 'Please try again or contact us for help.',
          color: '#F56565',
        };
    }
  };

  const content = getContent();

  return (
    <div style={styles.container}>
      <div style={styles.card}>
        <div style={styles.emoji}>{content.emoji}</div>
        <h1 style={{ ...styles.title, color: content.color }}>{content.title}</h1>
        <p style={styles.message}>{content.message}</p>
        <p style={styles.subMessage}>{content.subMessage}</p>

        {(status === 'verified' || status === 'already_verified') && (
          <div style={styles.infoBox}>
            <p style={styles.infoTitle}>Your rights as a parent</p>
            <ul style={styles.infoList}>
              <li>You can request a copy of your child's data at any time</li>
              <li>You can ask us to delete all data at any time</li>
              <li>You can withdraw consent by contacting us</li>
            </ul>
          </div>
        )}

        <div style={styles.links}>
          <a href="/privacy" style={styles.link}>Privacy Policy</a>
          <span style={styles.dot}>·</span>
          <a href="/terms" style={styles.link}>Terms of Service</a>
          <span style={styles.dot}>·</span>
          <a href="mailto:rain@elevenup.xyz" style={styles.link}>Contact Us</a>
        </div>

        <p style={styles.footer}>© {new Date().getFullYear()} 11 Up Company Ltd</p>
      </div>
    </div>
  );
};

const styles: Record<string, React.CSSProperties> = {
  container: {
    minHeight: '100vh',
    backgroundColor: '#0f0f23',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 24,
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
  },
  card: {
    backgroundColor: 'rgba(255,255,255,0.04)',
    border: '1px solid rgba(255,255,255,0.1)',
    borderRadius: 20,
    padding: '48px 36px',
    maxWidth: 480,
    width: '100%',
    textAlign: 'center' as const,
  },
  emoji: {
    fontSize: 64,
    marginBottom: 20,
  },
  title: {
    fontSize: 28,
    fontWeight: 800,
    margin: '0 0 16px',
  },
  message: {
    fontSize: 16,
    lineHeight: 1.6,
    color: 'rgba(255,255,255,0.8)',
    margin: '0 0 12px',
  },
  subMessage: {
    fontSize: 14,
    lineHeight: 1.5,
    color: 'rgba(255,255,255,0.5)',
    margin: '0 0 32px',
  },
  infoBox: {
    backgroundColor: 'rgba(79, 204, 198, 0.06)',
    border: '1px solid rgba(79, 204, 198, 0.15)',
    borderRadius: 12,
    padding: '16px 20px',
    marginBottom: 32,
    textAlign: 'left' as const,
  },
  infoTitle: {
    fontSize: 13,
    fontWeight: 600,
    color: '#4FCCC6',
    margin: '0 0 8px',
  },
  infoList: {
    fontSize: 13,
    lineHeight: 1.8,
    color: 'rgba(255,255,255,0.6)',
    paddingLeft: 18,
    margin: 0,
  },
  links: {
    marginBottom: 16,
  },
  link: {
    color: 'rgba(255,255,255,0.4)',
    textDecoration: 'none',
    fontSize: 13,
  },
  dot: {
    color: 'rgba(255,255,255,0.2)',
    margin: '0 8px',
    fontSize: 13,
  },
  footer: {
    fontSize: 12,
    color: 'rgba(255,255,255,0.2)',
    margin: 0,
  },
};

export default ConsentResult;
